import {
	Box,
	Button,
	CardActions,
	CardContent,
	Grid,
	IconButton,
	InputAdornment,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { useFileUploader } from '@/hooks/useFileUploader';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { FileInfo } from '@/api/DTO/common.interface';
import {
	addEmoRequestDTO,
	InformationEmotionSupportDTO,
} from '@/api/DTO/information/emo.interface';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import LinkIcon from '@mui/icons-material/Link';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import {
	useCreateEmoMutation,
	useUpdateEmoMutation,
} from '@/api/information/emoApiSlice';

interface Props {
	type: FormType;
	details?: InformationEmotionSupportDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function EmoInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const { uploadFile } = useFileUploader();
	const [coverImageObj, setCoverImageObj] = useState<FileInfo | null>(null);
	const [pdfUrl, setPdfUrl] = useState<FileInfo | null>(null);
	const [currentTab, setCurrentTab] = useState('zh');
	const pdfInputRef = useRef<HTMLInputElement>(null);
	const [createEmo] = useCreateEmoMutation();
	const [updateEmo] = useUpdateEmoMutation();
	const imageInputRef = useRef<HTMLInputElement>(null);

	const typeList = [
		{ id: 0, name: t('emo.web') },
		{ id: 1, name: t('emo.pdf') },
	];

	const defaultValues: DefaultValues<addEmoRequestDTO> = {
		descriptionEn: details?.descriptionEn || '',
		descriptionZh: details?.descriptionZh || '',
		titleEn: details?.titleEn || '',
		titleZh: details?.titleZh || '',
		mediaType: details?.mediaType || 0,
		link: details?.mediaType == 0 && details?.link ? details.link : '',
	};

	useEffect(() => {
		if (details) {
			if (details.coverImage) setCoverImageObj(details.coverImage);
			if (details.mediaType == 1) {
				setPdfUrl(details?.pdf || null);
				methods.setValue('pdfFile', details.link);
			}
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		descriptionZh: Yup.string().required(t('errorMessage.required')),
		mediaType: Yup.string().required(t('errorMessage.pleaseSelect')),
		link: Yup.string()
			.nullable()
			.when('mediaType', {
				is: (mediaType: string | number) => mediaType == 0,
				then: (schema) =>
					schema
						.required(t('errorMessage.required'))
						.url(t('errorMessage.url')),
			}),
		pdfFile: Yup.string()
			.nullable()
			.when('mediaType', {
				is: (mediaType: string | number) => mediaType == 1,
				then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			}),
	});
	const methods = useForm<addEmoRequestDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const watchType = methods.watch('mediaType');

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<addEmoRequestDTO> = async (data) => {
		const obj = { ...data };
		if (data.mediaType == 1) {
			obj.link = data.pdfFile;
			obj.pdf = pdfUrl;
		}
		if (coverImageObj) {
			obj.coverImage = coverImageObj;
		}
		const response =
			type == FormType.create
				? await createEmo(obj)
				: await updateEmo({ ...obj, id: details?.id });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createItemSuccess')
					: t('snackbar.updateItemSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			navigate('/information-manage/emo-support-list');
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate('/information-manage/emo-support-list');
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			setCoverImageObj(res);
		}
	};

	const handleDeleteImage = () => {
		setCoverImageObj(null);
		imageInputRef.current && (imageInputRef.current.value = '');
	};

	const handleClearPdf = () => {
		methods.setValue('pdfFile', null);
		pdfInputRef.current && (pdfInputRef.current.value = '');
		setPdfUrl(null);
	};

	const handlePdfChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;
		const res = await uploadFile(selectedFile);
		if (res) {
			methods.setValue('pdfFile', res.name);
			methods.trigger('pdfFile');

			setPdfUrl(res);

			enqueueSnackbar(t('snackbar.uploadSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={<Typography variant='body1'>{t('emo.zh')}</Typography>}
								value='zh'
							/>
							<Tab
								label={<Typography variant='body1'>{t('emo.en')}</Typography>}
								value='en'
							/>
						</Tabs>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('emo.title')}
											maxLength={20}
										/>
									</Grid>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='descriptionZh'
											label={t('emo.description')}
											maxLength={50}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('emo.title_')}
											maxLength={100}
										/>
									</Grid>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='descriptionEn'
											label={t('emo.description_')}
											maxLength={250}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('emo.coverImage_')}
								</Typography>
								<Box sx={{ margin: '10px 0' }}>
									{coverImageObj?.url && (
										<img
											src={coverImageObj?.url}
											width={110}
											height={132}
											style={{
												objectFit: 'contain',
											}}
										/>
									)}
								</Box>
								{coverImageObj?.url ? (
									<Button
										component='label'
										sx={{ height: 24 }}
										endIcon={<LoopOutlinedIcon />}
									>
										{t('emo.changeImage')}
										<VisuallyHiddenInput
											type='file'
											name='thumbnail'
											accept='image/*'
											onChange={handleUpload}
											ref={imageInputRef}
										/>
									</Button>
								) : (
									<Button
										component='label'
										sx={{ height: 24 }}
										endIcon={<DriveFolderUploadOutlinedIcon />}
									>
										{t('emo.uploadImage')}
										<VisuallyHiddenInput
											type='file'
											name='thumbnail'
											accept='image/*'
											onChange={handleUpload}
											ref={imageInputRef}
										/>
									</Button>
								)}
								{coverImageObj && (
									<Button
										component='label'
										sx={{ height: 24, marginLeft: '24px' }}
										color='error'
										endIcon={<DeleteOutlinedIcon />}
										onClick={handleDeleteImage}
									>
										{t('emo.deleteImage')}
									</Button>
								)}
							</Grid>
							<Grid item xs={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('emo.type')}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='mediaType'
									row
									options={typeList}
								/>
							</Grid>
							{watchType == 0 && (
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='link'
										label={t('emo.webLink')}
										InputProps={{
											endAdornment: (
												<>
													<InputAdornment position='end'>
														<LinkIcon />
													</InputAdornment>
												</>
											),
										}}
									/>
								</Grid>
							)}
							{watchType == 1 && (
								<Grid item xs={12}>
									<AppTextField
										disabled
										control={methods.control}
										name='pdfFile'
										label={t('emo.pdfFile')}
										InputProps={{
											endAdornment: (
												<InputAdornment position='end'>
													{pdfUrl?.url && (
														<IconButton type='button' onClick={handleClearPdf}>
															<CloseIcon />
														</IconButton>
													)}

													<IconButton component='label'>
														<AttachFileIcon />
														<VisuallyHiddenInput
															type='file'
															accept='.pdf'
															onChange={handlePdfChange}
															ref={pdfInputRef}
														/>
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</Grid>
							)}
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
